import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  MatSnackBar,
  MatSnackBarHorizontalPosition,
  MatSnackBarVerticalPosition,
} from '@angular/material/snack-bar';
import { AuthorizedApiService } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import {
  ColumnDefinition,
  MasterTableComponent,
  TableAction,
  TableFilterDefinition,
} from '@shared/components/master-table/master-table.component';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { CompanyDirectoryService } from '@shared/services/company-directory.service';
import { firstValueFrom } from 'rxjs';
import { JourneyConfig } from './journey.interface';
import { CompaniesRecord } from '../../organization/companies-records/companies-record.interface';

interface CompaniesRecordsResponse {
  items?: CompaniesRecord[];
  data?: CompaniesRecord[];
  results?: CompaniesRecord[];
  companiesRecords?: CompaniesRecord[];
}

@Component({
  selector: 'app-journey',
  standalone: true,
  imports: [BreadcrumbComponent, MasterTableComponent, TranslateModule, MatCardModule],
  templateUrl: './journey.component.html',
  styleUrl: './journey.component.scss',
})
export class JourneyComponent implements OnInit {
  private readonly apiService = inject(AuthorizedApiService);
  private readonly companyDirectory = inject(CompanyDirectoryService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  dataSource = new MatTableDataSource<JourneyConfig>([]);
  allJourneys: JourneyConfig[] = [];
  companies: CompaniesRecord[] = [];
  isLoading = true;
  actions: TableAction<JourneyConfig>[] = [];
  columnDefinitions: ColumnDefinition[] = [];
  tableFilters: TableFilterDefinition<JourneyConfig>[] = [];
  activeSearch = '';
  activeFilters: Record<string, string | number | boolean | null> = {};
  private companyNamesById = new Map<string, string>();

  ngOnInit(): void {
    this.configureTranslations();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.configureTranslations();
        this.allJourneys = this.allJourneys.map((journey) => this.normalizeJourney(journey));
        this.buildTableFilters();
        this.applyTableFilters();
      });
    void this.loadData();
  }

  get journeys(): JourneyConfig[] {
    return this.dataSource.data;
  }

  get activeJourneysCount(): number {
    return this.journeys.filter((journey) => journey.isActive).length;
  }

  get journeysWithMealBreak(): number {
    return this.journeys.filter((journey) => journey.includesMealBreak).length;
  }

  get averageWorkMinutes(): string {
    if (!this.journeys.length) {
      return this.getWorkMinutesLabel(0);
    }

    const total = this.journeys.reduce((sum, journey) => sum + journey.workMinutes, 0);
    return this.getWorkMinutesLabel(Math.round(total / this.journeys.length));
  }

  async loadData(): Promise<void> {
    this.isLoading = true;
    try {
      const [records, companiesResponse] = await Promise.all([
        firstValueFrom(this.apiService.get<JourneyConfig[]>('labor-config/journeys')),
        this.companies.length
          ? Promise.resolve(this.companies as CompaniesRecord[] | CompaniesRecordsResponse)
          : this.companyDirectory.getAllActive(),
      ]);
      this.companies = this.normalizeCompanies(companiesResponse)
        .filter((company) => company.isActive !== false)
        .sort((left, right) => left.name.localeCompare(right.name));
      this.companyNamesById = new Map(
        this.companies.map((company) => [company.companiesRecordId, company.name])
      );
      this.allJourneys = (records || []).map((journey) => this.normalizeJourney(journey));
      this.buildTableFilters();
      this.applyTableFilters();
    } catch (error) {
      console.error('Error loading journeys', error);
      this.allJourneys = [];
      this.companies = [];
      this.companyNamesById.clear();
      this.tableFilters = [];
      this.dataSource.data = [];
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('journeysCatalog.notifications.loadError')),
        'bottom',
        'center'
      );
    } finally {
      this.isLoading = false;
    }
  }

  handleAdd(): void {
    if (!this.hasAccess('create')) {
      return;
    }
    void this.router.navigate(['/attendance/journeys/new']);
  }

  async handleEdit(row: JourneyConfig): Promise<void> {
    if (!this.hasAccess('update')) {
      return;
    }
    await this.router.navigate(['/attendance/journeys', row.journeyId, 'edit'], {
      queryParams: row.companiesRecordId
        ? { companyId: row.companiesRecordId }
        : undefined,
    });
  }

  async handleView(row: JourneyConfig): Promise<void> {
    if (!this.hasAccess('read')) {
      return;
    }
    await this.router.navigate(['/attendance/journeys', row.journeyId]);
  }

  handleDelete(row: JourneyConfig): void {
    if (!this.hasAccess('delete')) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('journeysCatalog.dialog.deleteTitle'),
        message: this.t('journeysCatalog.dialog.deleteMessage', { journey: row.name }),
        confirmLabel: this.t('journeysCatalog.dialog.deleteConfirm'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.isLoading = true;
      try {
        await firstValueFrom(this.apiService.delete(`labor-config/journeys/${row.journeyId}`));
        await this.loadData();
        this.showNotification('snackbar-success', this.t('journeysCatalog.notifications.deleted'), 'bottom', 'center');
      } catch (error) {
        console.error('Error deleting journey', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('journeysCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isLoading = false;
      }
    });
  }

  handleBulkDelete(rows: JourneyConfig[]): void {
    if (!this.hasAccess('delete') || !rows.length) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('common.bulkDeleteTitle'),
        message: this.t('common.bulkDeleteMessage', { count: rows.length }),
        confirmLabel: this.t('common.delete'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.isLoading = true;
      try {
        await Promise.all(rows.map((row) => firstValueFrom(this.apiService.delete(`labor-config/journeys/${row.journeyId}`))));
        await this.loadData();
        this.showNotification('snackbar-success', this.t('common.bulkDeleteSuccess', { count: rows.length }), 'bottom', 'center');
      } catch (error) {
        console.error('Error deleting journeys', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('journeysCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isLoading = false;
      }
    });
  }

  onSearchChange(search: string): void {
    this.activeSearch = search.trim().toLowerCase();
    this.applyTableFilters();
  }

  onFiltersChange(filters: Record<string, string | number | boolean | null>): void {
    this.activeFilters = filters;
    this.applyTableFilters();
  }

  private normalizeJourney(journey: Partial<JourneyConfig>): JourneyConfig {
    const includesMealBreak = !!journey.includesMealBreak;
    const workMinutes = Number(journey.workMinutes || 0);
    const mealBreakMinutes = Number(journey.mealBreakMinutes || 0);
    const journeyType = this.normalizeOptionalText(journey.journeyType) || null;
    const isActive = journey.isActive !== false;

    return {
      journeyId: journey.journeyId || crypto.randomUUID(),
      center: journey.center || '',
      companiesRecordId: journey.companiesRecordId,
      name: journey.name || '',
      workMinutes,
      includesMealBreak,
      mealBreakMinutes,
      journeyType,
      isActive,
      workMinutesLabel: this.getWorkMinutesLabel(workMinutes),
      mealBreakLabel: includesMealBreak
        ? this.t('journeysCatalog.labels.mealBreakMinutes', { minutes: mealBreakMinutes })
        : this.t('journeysCatalog.labels.withoutMealBreak'),
      journeyTypeLabel: journeyType || this.t('journeysCatalog.labels.freeType'),
      companyLabel: journey.companiesRecordId
        ? this.companyNamesById.get(journey.companiesRecordId) || journey.companiesRecordId
        : '-',
      activeLabel: isActive ? this.t('common.status.active') : this.t('common.status.inactive'),
    };
  }

  private configureTranslations(): void {
    this.actions = [
      {
        id: 'add',
        icon: 'add',
        tooltip: this.t('journeysCatalog.actions.create'),
        colorClass: 'success',
        auth: { resource: 'journeys', action: 'create' },
        behavior: 'add',
      },
      {
        id: 'refresh',
        icon: 'refresh',
        tooltip: this.t('common.reload'),
        colorClass: 'neutral',
        auth: { resource: 'journeys', action: 'read' },
        behavior: 'refresh',
      },
      {
        id: 'details',
        icon: 'visibility',
        tooltip: this.t('common.select'),
        colorClass: 'info',
        auth: { resource: 'journeys', action: 'read' },
        selectionMode: 'single',
      },
      {
        id: 'edit',
        icon: 'edit',
        tooltip: this.t('common.update'),
        colorClass: 'accent',
        auth: { resource: 'journeys', action: 'update' },
        behavior: 'edit',
        selectionMode: 'single',
      },
      {
        id: 'delete',
        icon: 'delete_outline',
        tooltip: this.t('common.delete'),
        colorClass: 'danger',
        auth: { resource: 'journeys', action: 'delete' },
        behavior: 'delete',
        selectionMode: 'single',
      },
    ];

    this.columnDefinitions = [
      { def: 'select', label: this.t('common.select'), type: 'check', visible: true },
      { def: 'name', label: this.t('journeysCatalog.fields.name'), type: 'text', visible: true },
      { def: 'companyLabel', label: this.t('orgUnitsCatalog.fields.company'), type: 'text', visible: true },
      { def: 'workMinutesLabel', label: this.t('journeysCatalog.fields.workMinutes'), type: 'text', visible: true },
      { def: 'mealBreakLabel', label: this.t('journeysCatalog.fields.includesMealBreak'), type: 'text', visible: true },
      { def: 'journeyTypeLabel', label: this.t('journeysCatalog.fields.journeyType'), type: 'text', visible: true },
      {
        def: 'activeLabel',
        label: this.t('journeysCatalog.fields.status'),
        type: 'status',
        visible: true,
        statusBadgeMap: {
          [this.t('common.status.active')]: 'badge badge-solid-green',
          [this.t('common.status.inactive')]: 'badge badge-solid-red',
        },
      },
    ];

    this.dataSource.filterPredicate = (journey, filter) =>
      [journey.name, journey.companyLabel, journey.workMinutesLabel, journey.mealBreakLabel, journey.journeyTypeLabel, journey.activeLabel]
        .join(' ')
        .toLowerCase()
        .includes(filter);
  }

  private buildTableFilters(): void {
    this.tableFilters = [
      {
        id: 'company',
        label: this.t('orgUnitsCatalog.fields.company'),
        icon: 'business',
        value: this.activeFilters['company'] ?? null,
        searchable: true,
        options: this.companies.map((company) => ({
          value: company.companiesRecordId,
          label: company.name,
          searchText: `${company.code || ''} ${company.center || ''}`.trim(),
        })),
      },
    ];
  }

  private applyTableFilters(): void {
    const filtered = this.allJourneys.filter((journey) => {
      const matchesSearch =
        !this.activeSearch ||
        [journey.name, journey.companyLabel, journey.workMinutesLabel, journey.mealBreakLabel, journey.journeyTypeLabel, journey.activeLabel]
          .join(' ')
          .toLowerCase()
          .includes(this.activeSearch);
      const matchesCompany =
        !this.activeFilters['company'] ||
        journey.companiesRecordId === this.activeFilters['company'];

      return matchesSearch && matchesCompany;
    });

    this.dataSource.data = filtered;
  }

  private getWorkMinutesLabel(value: number): string {
    if (!value) {
      return this.t('journeysCatalog.labels.noWorkMinutes');
    }

    const hours = Math.floor(value / 60);
    const minutes = value % 60;
    if (!minutes) {
      return this.t('journeysCatalog.labels.hoursExact', { hours });
    }

    return this.t('journeysCatalog.labels.hoursMinutes', { hours, minutes });
  }

  private normalizeOptionalText(value: unknown): string | undefined {
    if (typeof value !== 'string') {
      return undefined;
    }
    const normalizedValue = value.trim();
    return normalizedValue || undefined;
  }

  private normalizeCompanies(
    response: CompaniesRecord[] | CompaniesRecordsResponse | null | undefined
  ): CompaniesRecord[] {
    if (Array.isArray(response)) {
      return response;
    }

    return response?.items || response?.data || response?.results || response?.companiesRecords || [];
  }

  private hasAccess(action: 'create' | 'read' | 'update' | 'delete' | 'access'): boolean {
    return this.authorizationService.hasAccess({ resource: 'journeys', action });
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 2500,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}
